// Tooltip — hover/focus-revealed text label on the React Aria foundation
// (ADR-029). React Aria owns the behavior we used to hand-roll: collision-aware
// positioning (flips + shifts so the tip never runs off-screen), hover/focus
// reveal with warm-up, Escape-to-dismiss, and RTL-aware anchoring (placement
// flips under dir="rtl" with no per-call work). We own 100% of the markup +
// tokens (COMPONENTS §Hand-rolled vs headless).
//
// A11Y — React Aria associates the tooltip as the trigger's accessible
// DESCRIPTION (aria-describedby), the idiomatic model. For an icon control whose
// aria-label already equals the tip text, a screen reader announces the name and
// then the same word as its description — benign reinforcement (architect-
// accepted). See COMPONENTS §Per-primitive WCAG (Tooltip).
//
// SCOPE — an ICON'S NAME, revealed on hover and focus (ADR-080 §12 · COMPONENTS
// §Overlay patterns): an icon button's label, a collapsed rail's labels, a
// glyph's name. Nothing lives ONLY in a tooltip — a phone has no hover — so
// words that matter (help, a folded list, a breakdown) go behind a tap target,
// `InfoPopover`. `Tooltip.tripwire.test.ts` names the files that may hold one.
//
// API — composition, not a render-prop: put the (focusable) trigger inside.
//   <Tooltip content="Settings"><IconButton aria-label="Settings">…</IconButton></Tooltip>
// The trigger must forward its ref + spread DOM props (our IconButton / NavLink /
// Button do). `Focusable` lets any such element drive the trigger.

import { TooltipTrigger, Tooltip as AriaTooltip, Focusable } from 'react-aria-components';
import type { TooltipProps as AriaTooltipProps } from 'react-aria-components';
import type { ComponentProps, ReactElement, ReactNode } from 'react';

// The element type Focusable accepts (ReactElement<DOMAttributes, string>) —
// derived from the component so we don't import @react-types/shared directly.
type FocusableChild = ComponentProps<typeof Focusable>['children'];

export type TooltipPlacement = NonNullable<AriaTooltipProps['placement']>;

export interface TooltipProps {
  /** The label/hint shown on hover + focus (also the trigger's a11y description). */
  content: ReactNode;
  /**
   * Placement relative to the trigger. Prefer the logical `start`/`end` (they
   * flip under RTL); `left`/`right` are physical. Default `top`.
   */
  placement?: TooltipPlacement;
  /** Hover warm-up before showing, ms (focus always reveals instantly). */
  delay?: number;
  /** The single focusable trigger element (forwards ref + spreads DOM props). */
  children: ReactElement;
}

export function Tooltip({ content, placement = 'top', delay = 400, children }: TooltipProps) {
  return (
    <TooltipTrigger delay={delay}>
      <Focusable>{children as FocusableChild}</Focusable>
      <AriaTooltip
        placement={placement}
        offset={8}
        // Inverted tokens (page fg/bg swapped) → high contrast in both modes by
        // construction. z-tooltip per DESIGN-TOKENS §Layering. React Aria injects
        // a maxHeight to keep the tip on-screen; `overflow-y-auto` makes tall
        // content (e.g. a full commitments readout) respect it instead of
        // bleeding past the rounded box near a viewport edge.
        className="z-tooltip pointer-events-none max-w-xs overflow-y-auto rounded-md bg-[var(--color-text-primary)] px-2 py-1 text-xs font-medium text-[var(--color-bg-page)] shadow-overlay data-[entering]:o-fade-in data-[exiting]:o-fade-out"
      >
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}
